(() => {
  const init = (story) => {
    const reader = story.querySelector('[data-haro-research-reader]');
    const nav = story.querySelector('[data-haro-research-tabs]');
    if (!reader || !nav || reader.dataset.haroReaderReady === 'true') return;
    reader.dataset.haroReaderReady = 'true';
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const links = [...nav.querySelectorAll('a')];
    const prose = reader.querySelector('.haro-review-prose');
    const panels = new Map([...reader.querySelectorAll('.haro-research-section')].map((p) => [p.id, p]));
    const pending = new Map();
    const initialTitle = 'Eye Floater Supplement Research & Studies | Haro Health';
    const initialDescription = 'Review clinical studies on eye floater supplements, fruit enzymes and targeted nutrients. Explore findings, study limitations and Haro Clear’s formula.';
    const initialCanonical = location.origin + '/pages/studies';
    let activeId = story.dataset.haroActiveTopic || location.hash.slice(1) || 'HaroResearchEnzymes';
    let sequence = 0;
    const idOf = (link) => link.dataset.haroTopicId || new URL(link.href).hash.slice(1);
    const urlOf = (link) => {
      const url = new URL(link.href);
      if (link.dataset.haroCanonical) url.hash = '';
      return url;
    };
    const fetchPanels = (url) => {
      const key = url.origin + url.pathname + url.search;
      if (!pending.has(key)) pending.set(key, fetch(key, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Study unavailable');
        return response.text();
      }).then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-haro-research-reader]');
        if (!source) throw new Error('Missing study');
        source.querySelectorAll('.haro-research-section').forEach((panel) => {
          if (!panels.has(panel.id)) {
            panel.hidden = true;
            const imported = document.importNode(panel, true);
            prose.append(imported);
            panels.set(panel.id, imported);
          }
        });
      }).catch((error) => { pending.delete(key); throw error; }));
      return pending.get(key);
    };
    const setMeta = (link) => {
      const title = link.dataset.haroTitle || initialTitle;
      const description = link.dataset.haroDescription || initialDescription;
      const canonical = link.dataset.haroCanonical || initialCanonical.replace(/\?.*$/, '');
      document.title = title;
      const preview = urlOf(link).searchParams.has('view');
      let robots = document.querySelector('meta[name="robots"]');
      if (preview) { if (!robots) { robots = document.createElement('meta'); robots.name = 'robots'; document.head.append(robots); } robots.content = 'noindex, follow'; }
      else if (robots?.content === 'noindex, follow') robots.remove();
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.content = description;
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) canonicalLink.href = canonical;
      for (const [selector, value] of [['meta[property="og:title"]', title], ['meta[property="og:description"]', description], ['meta[property="og:url"]', canonical]]) {
        const element = document.querySelector(selector);
        if (element) element.content = value;
      }
      let schema = document.querySelector('script[data-haro-active-study-schema]');
      if (link.dataset.haroCanonical) {
        if (!schema) { schema = document.createElement('script'); schema.type = 'application/ld+json'; schema.dataset.haroActiveStudySchema = ''; document.head.append(schema); }
        schema.textContent = JSON.stringify({ '@context': 'https://schema.org', '@type': 'WebPage', name: title, description, url: canonical, isPartOf: { '@type': 'CollectionPage', url: location.origin + '/pages/studies' }, publisher: { '@type': 'Organization', name: 'Haro Health', url: location.origin } });
      } else if (schema) schema.remove();
    };
    const scrollPanel = (panel) => {
      const header = document.querySelector('header-component .theme__header');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const top = Math.max(0, window.scrollY + panel.getBoundingClientRect().top - headerHeight - 20);
      window.scrollTo({ top, behavior: 'instant' });
    };
    const scrollTopics = (smooth = false) => {
      const menu = story.querySelector('#HaroResearchTopics');
      if (!menu) return;
      story.querySelectorAll('[data-haro-overview-intro]').forEach((e) => { e.hidden = false; });
      const header = document.querySelector('header-component .theme__header');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const top = Math.max(0, window.scrollY + menu.getBoundingClientRect().top - headerHeight - 20);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: smooth && !reducedMotion ? 'smooth' : 'instant' });
    };
    const evidenceLink = story.querySelector('.haro-research-story__hero a[href="#HaroResearchTopics"]');
    evidenceLink?.addEventListener('click', (event) => {
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (location.hash !== '#HaroResearchTopics') window.history.pushState({ haroOverview: true }, '', '#HaroResearchTopics');
      scrollTopics(true);
    });
    const show = (link, scroll) => {
      const id = idOf(link);
      activeId = id;
      story.classList.add('is-research-tabs-ready');
      story.querySelectorAll('[data-haro-overview-intro]').forEach((e) => { e.hidden = scroll; });
      panels.forEach((panel, panelId) => { panel.hidden = panelId !== id; panel.classList.toggle('is-active', panelId === id); });
      links.forEach((a) => {
        const active = idOf(a) === id;
        a.classList.toggle('is-active', active);
        a.setAttribute('aria-selected', String(active));
        a.tabIndex = active ? 0 : -1;
        if (active) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      });
      const panel = panels.get(id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', link.id);
      link.setAttribute('aria-controls', id);
      panel.querySelectorAll('[data-aos]').forEach((e) => e.classList.add('aos-animate'));
      panel.querySelectorAll('compare-images').forEach((e) => { e.classList.add('aos-animate'); if (typeof e.setOverlapImageSize === 'function') e.setOverlapImageSize(); });
      if (scroll) scrollPanel(panel);
      document.dispatchEvent(new Event('theme:resize'));
      return panel;
    };
    const go = async (link, history = true, focusTab = false) => {
      const ticket = ++sequence;
      const start = performance.now();
      const id = idOf(link);
      const url = urlOf(link);
      try {
        if (!panels.has(id)) {
          reader.setAttribute('aria-busy', 'true');
          await fetchPanels(url);
        }
        if (ticket !== sequence) return;
        if (!panels.has(id)) throw new Error('Missing selected study');
        if (history) window.history.pushState({ haroTopic: id }, '', url);
        setMeta(link);
        const panel = show(link, true);
        const heading = panel.querySelector('h2,h1');
        if (focusTab) link.focus({ preventScroll: true });
        else if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
        performance.measure('haro-study-switch', { start, end: performance.now(), detail: { topic: id } });
      } catch {
        if (ticket === sequence) window.location.assign(url.href);
      } finally { if (ticket === sequence) reader.removeAttribute('aria-busy'); }
    };
    nav.setAttribute('role', 'tablist');
    links.forEach((link, index) => {
      const id = idOf(link);
      link.id = 'HaroResearchTab' + (index + 1);
      link.setAttribute('role', 'tab');
      if (panels.has(id)) link.setAttribute('aria-controls', id);
      link.addEventListener('click', (event) => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); go(link);
      });
      link.addEventListener('pointerenter', () => { if (!panels.has(id)) fetchPanels(urlOf(link)).catch(() => {}); });
      link.addEventListener('focus', () => { if (!panels.has(id)) fetchPanels(urlOf(link)).catch(() => {}); });
      link.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % links.length;
        else if (event.key === 'ArrowLeft') next = (index + links.length - 1) % links.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = links.length - 1;
        else return;
        event.preventDefault(); go(links[next], true, true);
      });
    });
    reader.addEventListener('click', (event) => {
      const link = event.target.closest('.haro-research-panel-nav a');
      if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = links.find((a) => new URL(a.href).href === new URL(link.href).href);
      if (target) { event.preventDefault(); go(target); }
    });
    const currentLink = links.find((link) => idOf(link) === activeId) || links[0];
    const overviewTarget = location.hash === '#HaroResearchTopics';
    show(currentLink, !overviewTarget && (!!story.dataset.haroActiveTopic || panels.has(location.hash.slice(1))));
    if (overviewTarget) scrollTopics();
    window.addEventListener('popstate', (event) => {
      if (location.hash === '#HaroResearchTopics') { scrollTopics(); return; }
      const target = links.find((link) => idOf(link) === event.state?.haroTopic || idOf(link) === location.hash.slice(1) || urlOf(link).href === location.href);
      go(target || links[0], false);
    });
    const warm = () => {
      const urls = new Map(links.filter((a) => !panels.has(idOf(a))).map((a) => [urlOf(a).pathname + urlOf(a).search, urlOf(a)]));
      urls.forEach((url) => { fetchPanels(url).catch(() => {}); });
    };
    const warmAfterLoad = () => {
      if (navigator.connection?.saveData || /(^|slow-)2g|3g/.test(navigator.connection?.effectiveType || '')) return;
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm); else window.setTimeout(warm, 500);
    };
    if (document.readyState === 'complete') warmAfterLoad();
    else window.addEventListener('load', warmAfterLoad, { once: true });
  };
  const initAll = () => document.querySelectorAll('[data-haro-topic-routes]').forEach(init);
  if (document.querySelector('[data-haro-topic-routes]')) initAll();
  else document.addEventListener('DOMContentLoaded', initAll, { once: true });
  document.addEventListener('shopify:section:load', initAll);
})();
